import React, { Suspense, lazy, useEffect } from "react";
import { ToneMappingMode } from "postprocessing";
import { Canvas } from "@react-three/fiber";
import { ToneMapping, EffectComposer } from "@react-three/postprocessing";

import LoadingBar from "../components/LoadingBar";
import { Bvh, Environment, PerspectiveCamera } from "@react-three/drei";
import { folder, Leva, useControls } from "leva";
import { Route, useRoute } from "wouter";
import ArtworkDetailView from "./ArtworkDetail";
import ArnhemLogo from "../components/ArnhemLogo";
import ViewHistoryButton from "../components/buttons/ViewHistoryButton";
import { useStore } from "../store/store";
import HistoryView from "./History";
import _ from "lodash";
import CCLogo from "../components/CCLogo";
import IntroView from "./Intro";
import ArtworkSelectorView from "./ArtworkSelector";
import SearchButton from "../components/buttons/SearchButton";
import SortingButton from "../components/buttons/SortingButton";
import HelpButton from "../components/buttons/HelpButton";
import InfoButton from "../components/buttons/InfoButton";
import audioManager from "../audio";
import AudioButton from "../components/buttons/AudioButton";
import ShepherdTour from "../components/ShepherdTour";
import PageTransition from "../page/PageTransition";
import SearchView from "./Search";
import ConnectionView from "./Connection";
import { isDebugMode } from "../config";
import TimelineView from "./Timeline";
import CategoriesView from "./Sorting/Categories";
import InfoModal from "../components/modals/InfoModal";

const Scene = lazy(() => import("./Scene/Scene"));
const App: React.FC = () => {
  const {
    setArtworkInFront,
    setSelectedArtwork,
    setAudioManager,
    isTourOpen,
    setTourOpen,
    isInfoOpen,
    setInfoOpen,
  } = useStore();
  const [isCollectionView] = useRoute("/collection");
  const _audioManager = audioManager;

  useEffect(() => {
    _audioManager.initialize().then(() => setAudioManager(_audioManager));
  }, []);

  // Note: This is working clearly!
  useEffect(() => {
    if (isCollectionView) {
      setArtworkInFront(false);
      setSelectedArtwork(null);
    }
  }, [isCollectionView, setArtworkInFront, setSelectedArtwork]);

  const toneMappingOptions = _.mapValues(ToneMappingMode, (value) => value);
  const { toneMappingMode } = useControls(
    "App", // Main folder name
    {
      ToneMapping: folder({
        toneMappingMode: {
          value: ToneMappingMode.NEUTRAL,
          options: toneMappingOptions,
        },
      }),
    },
  );

  return (
    <>
      <div className="w-full h-screen flex flex-col">
        <Canvas shadows gl={{ antialias: true, toneMappingExposure: 1.0 }}>
          <fogExp2 attach="fog" args={["black", 0.03]} />
          <ambientLight intensity={7.7} />
          <Environment preset="city" />
          <PerspectiveCamera makeDefault position={[0, 0, 0]} fov={75}>
            <Suspense fallback={<LoadingBar />}>
              <Bvh firstHitOnly>
                <Scene />
              </Bvh>
            </Suspense>
          </PerspectiveCamera>
          <EffectComposer>
            <ToneMapping mode={toneMappingMode} />
          </EffectComposer>
          {/* <Perf position="top-left" /> */}
        </Canvas>
        <Leva hidden={!isDebugMode} />
      </div>
      <Route path={"/"}>
        <PageTransition>
          <IntroView />
        </PageTransition>
      </Route>
      <Route path={"/collection/:uuid"}>
        <ArtworkDetailView />
      </Route>
      <Route path={"/history"}>
        <HistoryView />
      </Route>
      <Route path={"/search"}>
        <SearchView />
      </Route>
      <Route path={"/collection/:uuid/connections"}>
        <ConnectionView />
      </Route>
      <Route path={"/sorting/categories"}>
        <CategoriesView />
      </Route>
      <Route path={"/timeline"}>
        <TimelineView />
      </Route>
      <Route path={"/picker"}>
        <ArtworkSelectorView />
      </Route>
      <Route path={"/collection"}>
        <ArnhemLogo />
        <CCLogo />
        <div className="fixed top-6 right-2 md:right-8 flex flex-col gap-3 items-end transition duration-700">
          <SearchButton />
          <ViewHistoryButton />
          <SortingButton />
        </div>
        <div className="fixed bottom-6 left-2 md:left-8 flex flex-col gap-3 items-start transition duration-700">
          <AudioButton />
          <HelpButton />
          <InfoButton />
        </div>
      </Route>
      <ShepherdTour isOpen={isTourOpen} onClose={() => setTourOpen(false)} />
      <InfoModal isOpen={isInfoOpen} onClose={() => setInfoOpen(false)} />
    </>
  );
};

export default App;
